import { bootstrapSheet } from '../../core/styles/bootstrap.js';
import { eumBaseSheet } from '../../core/styles/eum-base.js';
// @ts-ignore — handled by esbuild inline-css plugin
import componentSheet from './eum-person-avatar.css';

/**
 * <eum-person-avatar> — circular avatar with image and initials fallback.
 *
 * Driven via attributes:
 *  - image-url:    optional URL for the profile image.
 *  - display-name: used to derive initials and (deterministically) a background color.
 *  - has-image:    'true' to attempt loading image-url. When the image fails to load
 *                  (or has-image is false / no url), the initials are shown instead.
 *
 * Ports the legacy setInitials / setRandomBgColor behavior, but the background color
 * is derived deterministically from the display name so it stays stable across renders.
 */
export class EumPersonAvatar extends HTMLElement {
    private container!: HTMLElement;

    static get observedAttributes(): string[] {
        return ['image-url', 'display-name', 'has-image'];
    }

    connectedCallback(): void {
        if (!this.shadowRoot) {
            const shadow = this.attachShadow({ mode: 'open' });
            shadow.adoptedStyleSheets = [bootstrapSheet, eumBaseSheet, componentSheet];
            shadow.innerHTML = /*html*/ `<div class="person-avatar"></div>`;
            this.container = shadow.querySelector('.person-avatar')!;
        }
        this.render();
    }

    attributeChangedCallback(): void {
        if (this.container) this.render();
    }

    private get displayName(): string {
        return this.getAttribute('display-name') || '';
    }

    private get imageUrl(): string {
        return this.getAttribute('image-url') || '';
    }

    private get hasImage(): boolean {
        const raw = this.getAttribute('has-image');
        return raw === 'true' || raw === '';
    }

    private render(): void {
        const name = this.displayName;
        const initials = this.getInitials(name);

        this.container.style.backgroundColor = this.getColorForName(name);
        this.container.innerHTML = /*html*/ `<span class="initials">${this.escapeHTML(initials)}</span>`;

        if (this.hasImage && this.imageUrl) {
            const img = document.createElement('img');
            img.alt = name;
            // Remove the image on load error, falling back to the initials underneath.
            img.addEventListener('error', () => img.remove(), { once: true });
            img.src = this.imageUrl;
            this.container.appendChild(img);
        }
    }

    private getInitials(str: string): string {
        if (!str) return '';
        const parts = str.trim().split(/\s+/);
        const first = parts[0]?.charAt(0) ?? '';
        const last = parts.length > 1 ? parts[parts.length - 1].charAt(0) : '';
        return (first + last).toUpperCase();
    }

    /**
     * Deterministically derives an HSL background color from the name so the same
     * person always gets the same color (unlike the legacy random approach).
     */
    private getColorForName(name: string): string {
        let hash = 0;
        for (let i = 0; i < name.length; i++) {
            hash = name.charCodeAt(i) + ((hash << 5) - hash);
            hash |= 0;
        }
        const h = Math.abs(hash) % 360;
        const s = 30 + (Math.abs(hash >> 3) % 40); // 30–69
        const l = 60 + (Math.abs(hash >> 6) % 15); // 60–74
        return `hsl(${h}, ${s}%, ${l}%)`;
    }

    private escapeHTML(str: string): string {
        const div = document.createElement('div');
        div.textContent = str;
        return div.innerHTML;
    }
}

customElements.define('eum-person-avatar', EumPersonAvatar);
